
import React, { useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Search, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import { Lead } from '@/types/lead';
import { StatusBadge } from './websearch/StatusBadge';
import LoadingState from './websearch/LoadingState';
import WebSearchDataView from './websearch/WebSearchDataView';
import ProcessingState from './websearch/ProcessingState';
import { useWebSearch } from '@/hooks/useWebSearch';

interface WebSearchPanelProps {
  lead: Lead;
  onDataUpdated?: () => void;
  onEnrichmentStatusChange?: (status: string | null, isProcessing: boolean, queuePosition: number | null) => void;
}

const WebSearchPanel: React.FC<WebSearchPanelProps> = ({ lead, onDataUpdated, onEnrichmentStatusChange }) => {
  const {
    searchData,
    searchStatus,
    lastUpdated,
    queuedAt,
    isLoading,
    isProcessing,
    isInitialLoading,
    progressValue,
    queuePosition,
    handleRefresh,
    handleProcessQueue
  } = useWebSearch(lead, onDataUpdated);

  useEffect(() => {
    if (onEnrichmentStatusChange) {
      onEnrichmentStatusChange(searchStatus, isProcessing || isLoading, queuePosition);
    }
  }, [searchStatus, isProcessing, isLoading, queuePosition, onEnrichmentStatusChange]);

  if (isInitialLoading) {
    return <LoadingState />;
  }

  if (searchData) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center">
                <Search className="h-5 w-5 mr-2" />
                Company Insight
              </CardTitle>
              <CardDescription className="flex items-center">
                {lastUpdated && (
                  <span className="mr-2">Last updated {format(lastUpdated, 'MMM d, yyyy HH:mm')}</span>
                )}
                <StatusBadge status={searchStatus} />
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh} 
              disabled={isLoading || searchStatus === 'pending' || searchStatus === 'processing'}
              title="Refresh web information"
            >
              {isLoading || searchStatus === 'pending' || searchStatus === 'processing' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <WebSearchDataView searchData={searchData} leadName={lead.name} />
        </CardContent>
      </Card>
    );
  }

  if (!searchData && (searchStatus === 'pending' || searchStatus === 'processing')) {
    return (
      <ProcessingState
        searchStatus={searchStatus}
        progressValue={progressValue}
        queuePosition={queuePosition}
        queuedAt={queuedAt}
        isLoading={isLoading}
        isProcessing={isProcessing}
        onRefresh={handleRefresh}
        onProcessQueue={handleProcessQueue}
      />
    );
  }

  if (!searchData && searchStatus === 'failed') {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Company Insight
          </CardTitle>
          <CardDescription className="flex items-center">
            <span className="mr-2">Web search failed</span>
            <StatusBadge status={searchStatus} />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <AlertCircle className="h-10 w-10 mb-4 text-red-500" />
            <h3 className="text-lg font-medium mb-2">Web search failed</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              There was an error retrieving web information for this lead.
              You can try again using the button below.
            </p>
            <Button onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry Web Search
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <Search className="h-5 w-5 mr-2" />
          Company Insight
        </CardTitle>
        <CardDescription>
          Enrich this lead with information from the web
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Search className="h-10 w-10 mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No web information available</h3>
          <p className="text-muted-foreground mb-6 max-w-md">
            Get detailed information about this lead from across the web, including services, key people, reviews, and more.
          </p>
          <Button onClick={handleRefresh} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Searching the web...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Perform Web Search
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WebSearchPanel;
